import {useState, useRef} from 'react';
import {api} from '../lib/auth.js';
import {kr} from '../lib/format.js';

/**
 * Import a Handelsbanken statement and see what the bank has done that the books have not.
 *
 * The file is sent as base64 of the RAW BYTES, never as text. The export is ISO-8859-1, and
 * letting the browser decode it turns every å, ä and ö in a payee name into a replacement
 * character before the server ever sees it.
 *
 * Nothing is created by uploading. The preview is read-only; the chosen lines become shadow
 * vouchers only when the button is pressed, because a provisional entry nobody asked for is
 * exactly what rule 17 exists to prevent.
 */
export default function StatementImport({shadow, onCreated}) {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);
	const [picked, setPicked] = useState(() => new Set());
	const [accounts, setAccounts] = useState({});
	const fileRef = useRef(null);

	const upload = async file => {
		setBusy(true);
		setError(null);
		try {
			const buf = await file.arrayBuffer();
			let bin = '';
			const bytes = new Uint8Array(buf);
			// Chunked: String.fromCharCode.apply on 90 kB blows the argument limit.
			for (let i = 0; i < bytes.length; i += 8192) {
				bin += String.fromCharCode(...bytes.subarray(i, i + 8192));
			}
			const res = await api(`/api/statement/preview${shadow ? '?shadow=1' : ''}`, {
				method: 'POST',
				body: JSON.stringify({filename: file.name, content: btoa(bin)}),
			});
			const json = await res.json();
			if (!res.ok) throw new Error(json.error ?? `API svarade ${res.status}`);
			setData(json);
			// Nothing pre-selected: choosing what to book is the user's act, not a default.
			setPicked(new Set());
			setAccounts({});
		} catch (e) { setError(e.message); } finally { setBusy(false); }
	};

	const commit = async () => {
		// Indexed off the original array, not off the filtered one: the per-row account
		// override is keyed by position in `unmatched`, and re-deriving it after filtering
		// would pair the wrong account with the wrong line.
		const rows = data.unmatched
			.map((r, i) => (picked.has(i)
				? {date: r.date, amount: r.amount, text: r.text,
					bank_account: data.bank_account,
					account: Number(accounts[i] || data.suspense)}
				: null))
			.filter(Boolean);
		if (!rows.length) return;
		if (!window.confirm(`Skapa ${rows.length} skuggverifikat?`)) return;
		setBusy(true);
		try {
			const res = await api('/api/statement/commit', {
				method: 'POST',
				body: JSON.stringify({ledger_account: data.ledger_account, rows}),
			});
			const json = await res.json();
			if (!res.ok) throw new Error(json.error ?? `API svarade ${res.status}`);
			setError(json.failed?.length ? `${json.failed.length} rader misslyckades: ${json.failed[0].error}` : null);
			setData(null);
			setPicked(new Set());
			if (fileRef.current) fileRef.current.value = '';
			onCreated?.();
		} catch (e) { setError(e.message); } finally { setBusy(false); }
	};

	const toggle = i => setPicked(s => {
		const n = new Set(s);
		n.has(i) ? n.delete(i) : n.add(i);
		return n;
	});

	return (
		<div className="table-wrap">
			<div className="chart__head" style={{padding: '14px 16px 0'}}>
				<span className="chart__title">Läs in kontoutdrag</span>
				<span className="chart__hint">
					Handelsbanken CSV eller camt.053 — formatet känns igen automatiskt
				</span>
			</div>

			<div style={{padding: '12px 16px 16px'}}>
				<div className="fieldrow">
					<input ref={fileRef} type="file" accept=".csv,.xml,.txt" className="search"
						onChange={e => e.target.files?.[0] && upload(e.target.files[0])} />
					{busy && <span className="stamp">Läser…</span>}
				</div>
				{error && <p className="foot-note" style={{color: '#000', fontWeight: 600}}>{error}</p>}

				{data && (
					<>
						<div className="panel panel--note" style={{marginTop: 12}}>
							<div className="panel__title">
								{data.bank_account} → konto {data.ledger_account} · {data.from} – {data.to}
							</div>
							<p>
								{data.counts.rows} transaktioner: <b>{data.counts.matched} redan bokförda</b>,
								{' '}<b>{data.counts.unmatched} saknas i huvudboken</b> ({kr(data.totals.unmatched)} kr netto).
								{data.counts.ledger_only > 0 && ` ${data.counts.ledger_only} poster finns bara i huvudboken.`}
								{data.closing != null && ` Bankens utgående saldo är ${kr(data.closing)} kr.`}
							</p>
							{data.counts.pending > 0 && (
								<p>
									{data.counts.pending} preliminär(a) korttransaktion(er) om
									{' '}{kr(data.pending.reduce((a, b) => a + b.amount, 0))} kr är inte medräknade —
									de är reserverade men ännu inte bokförda av banken.
								</p>
							)}
						</div>

						{data.unmatched.length > 0 && (
							<>
								<div className="fieldrow" style={{margin: '12px 0 8px'}}>
									<button className="btn btn--sm"
										onClick={() => setPicked(new Set(data.unmatched.map((_, i) => i)))}>
										Markera alla
									</button>
									<button className="btn btn--sm" onClick={() => setPicked(new Set())}>
										Avmarkera
									</button>
									<span className="spacer" />
									<button className="btn btn--primary" disabled={busy || !picked.size}
										onClick={commit}>
										Skapa {picked.size || ''} skuggverifikat
									</button>
								</div>

								<table style={{minWidth: 640}}>
									<thead><tr>
										<th style={{width: 30}} />
										<th>Datum</th><th>Referens</th><th>Belopp</th><th>Motkonto</th>
									</tr></thead>
									<tbody>
										{data.unmatched.map((r, i) => (
											<tr key={`${r.date}-${i}`} className={picked.has(i) ? 'row--current' : undefined}>
												<td><input type="checkbox" checked={picked.has(i)} onChange={() => toggle(i)} /></td>
												<td>{r.date}</td>
												<td>{r.text}</td>
												<td className="num--strong">{kr(r.amount)}</td>
												<td>
													{/* Defaults to the OBS-konto: it makes the bank account right, which
													    is the point, while leaving the classification visibly owed
													    rather than guessed from a payee string. */}
													<input className="search cell" inputMode="numeric"
														placeholder={String(data.suspense)}
														value={accounts[i] ?? ''}
														onChange={e => setAccounts(a => ({...a, [i]: e.target.value}))} />
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</>
						)}

						{data.ledger_only.length > 0 && (
							<>
								<p className="foot-note" style={{marginTop: 14}}>
									<b>Finns bara i huvudboken</b> ({kr(data.totals.ledger_only)} kr) — bokfört men utan
									motsvarighet på kontoutdraget. Oftast en periodgräns; annars något bokfört på fel konto.
								</p>
								<table style={{minWidth: 520}}>
									<thead><tr><th>Datum</th><th>Verifikat</th><th>Beskrivning</th><th>Belopp</th></tr></thead>
									<tbody>
										{data.ledger_only.slice(0, 20).map((r, i) => (
											<tr key={`${r.voucher}-${i}`}>
												<td>{r.date}</td><td>{r.voucher}</td>
												<td>{r.description}</td>
												<td className="num--strong">{kr(r.amount)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</>
						)}
					</>
				)}
			</div>
		</div>
	);
}
